import React from 'react';
import { Mail, Phone, Smartphone, Globe, MapPin, Eye, Search, ShieldCheck, GraduationCap } from 'lucide-react';
import PreventiAppLogo from './PreventiAppLogo';
import { EMPRESA } from '../lib/legal';

const Footer = ({ onOpenLegal }) => {
  const year = new Date().getFullYear();
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer data-testid="footer" className="bg-deep text-white pt-16 sm:pt-20 pb-10 relative overflow-hidden">
      <div className="glow-violet" style={{ bottom: '-260px', left: '-200px', opacity: 0.3 }} aria-hidden />

      {/* Banner pilares */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8" data-testid="footer-pilares">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 items-center">
            <div className="lg:col-span-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#78A8FF]">Nuestra promesa</p>
              <p className="mt-2 text-lg font-bold text-white leading-snug">
                Observar hoy, prevenir mañana,<br />proteger siempre.
              </p>
            </div>
            <div className="lg:col-span-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: Eye, label: 'Observamos' },
                { icon: Search, label: 'Detectamos' },
                { icon: ShieldCheck, label: 'Prevenimos' },
                { icon: GraduationCap, label: 'Protegemos' },
              ].map((p) => (
                <div key={p.label} className="flex items-center gap-3 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3">
                  <span className="w-9 h-9 rounded-xl bg-[#78A8FF]/15 text-[#78A8FF] flex items-center justify-center">
                    <p.icon className="w-4 h-4" />
                  </span>
                  <span className="text-sm font-semibold text-white">{p.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* main */}
        <div className="mt-12 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <PreventiAppLogo className="h-12 w-auto" inverted />
            <p className="mt-5 text-base text-[#C8D8FF] max-w-md leading-relaxed">
              Centro de Inteligencia Preventiva Escolar. Transformamos observaciones cotidianas en información
              preventiva, trazable y útil para fortalecer la convivencia escolar.
            </p>
          </div>

          <div className="lg:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#78A8FF]">Navegación</p>
            <ul className="mt-4 space-y-2.5">
              {[
                { id: 'inicio', label: 'Inicio' },
                { id: 'problema', label: 'Problema' },
                { id: 'beneficios', label: 'Beneficios' },
                { id: 'como-funciona', label: 'Cómo funciona' },
                { id: 'piloto', label: 'Implementación piloto' },
                { id: 'contacto', label: 'Contacto' },
              ].map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`footer-link-${l.id}`}
                    onClick={() => go(l.id)}
                    className="text-sm text-white/85 hover:text-white transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#78A8FF]">Contacto</p>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#78A8FF] shrink-0" />
                <a href="mailto:demo@preventiapp.com" className="text-white hover:underline">demo@preventiapp.com</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#78A8FF] shrink-0" />
                <a href={`mailto:${EMPRESA.emailGeneral}`} className="text-white hover:underline">{EMPRESA.emailGeneral}</a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#78A8FF] shrink-0" />
                <a href="tel:+56229736035" className="text-white hover:underline">+56 2 2973 6035</a>
              </li>
              <li className="flex items-center gap-3">
                <Smartphone className="w-4 h-4 text-[#78A8FF] shrink-0" />
                <a href="https://wa.me/56996099788" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">+56 9 9609 9788 · WhatsApp</a>
              </li>
              <li className="flex items-center gap-3">
                <Globe className="w-4 h-4 text-[#78A8FF] shrink-0" />
                <span className="text-white">www.preventiapp.com</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#78A8FF] shrink-0" />
                <span className="text-white leading-snug">
                  {EMPRESA.direccion}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Aviso legal / datos empresa */}
        <div className="mt-10 rounded-2xl bg-white/[0.03] border border-white/10 px-5 py-4 sm:px-6 sm:py-5" data-testid="footer-empresa">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#78A8FF] mb-2">Datos del prestador</p>
          <p className="text-xs text-[#C8D8FF] leading-relaxed">
            <strong className="text-white">{EMPRESA.razonSocial}</strong> · RUT {EMPRESA.rut} ·{' '}
            {EMPRESA.direccion} · <a href={`mailto:${EMPRESA.emailGeneral}`} className="underline hover:text-white">
            {EMPRESA.emailGeneral}</a> · Privacidad:{' '}
            <a href={`mailto:${EMPRESA.emailPrivacidad}`} className="underline hover:text-white">
            {EMPRESA.emailPrivacidad}</a>
          </p>
        </div>

        <div className="mt-6 border-t border-white/10 pt-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <p className="text-xs text-[#C8D8FF]">
            © {year} PreventiApp · Centro de Inteligencia Preventiva Escolar. Todos los derechos reservados.
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#C8D8FF]">
            <button
              type="button"
              data-testid="footer-link-privacidad"
              onClick={() => onOpenLegal && onOpenLegal('privacidad')}
              className="hover:text-white underline underline-offset-2"
            >
              Política de Privacidad
            </button>
            <button
              type="button"
              data-testid="footer-link-terminos"
              onClick={() => onOpenLegal && onOpenLegal('terminos')}
              className="hover:text-white underline underline-offset-2"
            >
              Términos y Condiciones
            </button>
            <span>Hecho con propósito en Santiago de Chile.</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
